/**
 * Cart zero-quantity cleanup
 *
 * Sold-out products can remain in the cart as quantity-0 lines, which show as
 * "0" in the cart and can't be cleared with the remove button. This watches
 * the Alpine cart store (seeded on page load, then refreshed from /cart.js)
 * and, whenever it holds a quantity-0 line, re-reads the live cart and removes
 * those lines one at a time via /cart/change.js, then syncs the store.
 *
 * Lines are removed by 1-based line number taken from the freshly fetched
 * cart (re-read after every removal), so indexes never go stale. Each line key
 * is attempted once per page load so a failed removal can't loop.
 */
(() => {
  const headers = {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  const attempted = new Set();
  let running = false;

  const isZeroQuantity = (item) => Number(item.quantity) <= 0;

  const getCart = async () => {
    const res = await fetch(`${window.routes.cart_url}.js`, { credentials: 'same-origin', headers });
    return res.ok ? res.json() : null;
  };

  const removeLine = async (line) => {
    const res = await fetch(`${window.routes.cart_change_url}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers,
      body: JSON.stringify({ line, quantity: 0 }),
    });
    if (!res.ok) {
      console.warn(`Cart cleanup: could not remove line ${line} (status ${res.status})`);
      return null;
    }
    return res.json();
  };

  const removeZeroQuantityLines = async (store) => {
    if (running) return;
    running = true;

    try {
      let cart = await getCart();
      if (!cart) return;

      while (true) {
        const index = cart.items.findIndex((item) => isZeroQuantity(item) && !attempted.has(item.key));
        if (index === -1) break;

        attempted.add(cart.items[index].key);
        cart = (await removeLine(index + 1)) || cart;
      }

      store.setCart(cart);
    } catch (e) {
      console.error('Cart cleanup failed:', e);
    } finally {
      running = false;
    }
  };

  const start = () => {
    const store = window.Alpine && window.Alpine.store('cart');
    if (!store) return;

    // Re-runs whenever the store's items (or their quantities) change.
    window.Alpine.effect(() => {
      const items = Array.isArray(store.items) ? store.items : [];
      if (items.some((item) => isZeroQuantity(item) && !attempted.has(item.key))) {
        removeZeroQuantityLines(store);
      }
    });
  };

  if (window.Alpine && window.Alpine.store('cart')) {
    start();
  } else {
    document.addEventListener('alpine:initialized', start, { once: true });
  }
})();
